import { useContext, useState } from 'react'
import { ChevronDown, Copy, Check } from 'lucide-react'
import { QueryClientContext } from '@tanstack/react-query'
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select'
import { ProviderLogo } from '../ai/ProviderLogo'
import { useMyAi, useSaveMyAi, type Provider } from '../../hooks/use-ai-settings'
import type { TurnMeta } from '../../types/chat'

/**
 * The troubleshooting strip at the top of the sidebar.
 *
 * One line by default, because the sidebar is 400px and the chat is what people came
 * for. The thread id stays on it rather than behind a debug toggle: it is the thing
 * you paste into a bug report, and a toggle you have to switch on after the problem
 * has already happened is a toggle that is always off when it matters.
 */

/** 1840 → "1.84s"; 640 → "640ms". Short enough for the collapsed line. */
function ms(value: number | undefined): string {
  if (typeof value !== 'number') return '—'
  return value >= 1000 ? `${(value / 1000).toFixed(2)}s` : `${Math.round(value)}ms`
}

const num = (n: number | undefined) => (typeof n === 'number' ? n.toLocaleString('en-AU') : '—')

function CopyId({ value, label }: { value: string; label: string }) {
  const [copied, setCopied] = useState(false)

  const copy = async (e: React.MouseEvent) => {
    // The row itself toggles the panel, so the copy button must not also do that.
    e.stopPropagation()
    try {
      await navigator.clipboard.writeText(value)
      setCopied(true)
      setTimeout(() => setCopied(false), 1400)
    } catch {
      // Clipboard blocked (insecure context or denied permission). The full id is
      // already on screen in the expanded panel, so it can still be selected by hand.
      setCopied(false)
    }
  }

  return (
    <button
      type="button"
      onClick={(e) => void copy(e)}
      aria-label={copied ? `${label} copied` : `Copy ${label}`}
      className="inline-flex items-center gap-1 rounded p-0.5 text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
    >
      {copied ? <Check className="h-3 w-3 text-emerald-500" /> : <Copy className="h-3 w-3" />}
    </button>
  )
}

const ORG_DEFAULT = '__org_default__'

/**
 * The model picker, in the expanded panel where the model is already reported.
 * Options are the caller's allowed set from /settings/ai/me; hidden entirely
 * when only the org default is available (a one-option menu is noise). The
 * pick persists (PUT /settings/ai/me) and rides the next send as a
 * per-request override; the collapsed bar keeps proving what actually ran.
 */
function ModelPicker() {
  // Render nothing outside a QueryClientProvider (some tests mount the bar bare).
  const queryClient = useContext(QueryClientContext)
  if (!queryClient) return null
  return <ModelPickerInner />
}

function ModelPickerInner() {
  const { data: my } = useMyAi()
  const save = useSaveMyAi()

  if (!my || my.allowed.length <= 1) return null
  const providerLabel = (p: Provider) => my.catalog.find((c) => c.provider === p)?.label ?? p
  const current = my.selection ? `${my.selection.provider}:${my.selection.model}` : ORG_DEFAULT

  const handleChange = (v: string) => {
    if (v === ORG_DEFAULT) { save.mutate(null); return }
    const [provider, ...rest] = v.split(':')
    save.mutate({ provider: provider as Provider, model: rest.join(':') })
  }

  return (
    <>
      <dt className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Use</dt>
      <dd className="col-span-3 min-w-0">
        <Select value={current} onValueChange={handleChange} disabled={save.isPending}>
          <SelectTrigger
            aria-label="Model to use"
            className="h-6 w-full rounded border-input px-1.5 py-0 font-mono text-[11px] text-foreground"
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ORG_DEFAULT} className="font-mono text-[11px]">
              <span className="flex items-center gap-1.5">
                <ProviderLogo provider={my.org_default.provider} className="h-3 w-3 shrink-0" />
                org default · {my.org_default.model}
              </span>
            </SelectItem>
            {my.allowed.map((m) => (
              <SelectItem key={`${m.provider}:${m.model}`} value={`${m.provider}:${m.model}`} className="font-mono text-[11px]">
                <span className="flex items-center gap-1.5">
                  <ProviderLogo provider={m.provider} className="h-3 w-3 shrink-0" />
                  {providerLabel(m.provider)} · {m.model}
                </span>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </dd>
    </>
  )
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <>
      <dt className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">{label}</dt>
      <dd className="min-w-0 truncate font-mono text-[11px] tabular-nums text-foreground">{children}</dd>
    </>
  )
}

export function ChatMetricsBar({ meta, threadId }: { meta: TurnMeta | null; threadId: string }) {
  const [open, setOpen] = useState(false)

  // Before the first turn there are no numbers, but there IS a thread id — the one
  // the next message will be sent under. Showing it beats an empty bar.
  const id = meta?.threadId ?? threadId
  const short = id.slice(0, 8)

  return (
    <div className="border-b bg-muted/40 text-muted-foreground">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-controls="chat-metrics-panel"
        className="flex w-full items-center gap-2 px-3 py-1.5 text-left transition-colors hover:bg-muted/60 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-inset focus-visible:ring-ring"
      >
        <span className="font-mono text-[11px] text-foreground/80">{short}</span>
        <span aria-hidden className="h-3 w-px bg-border" />
        <span className="truncate font-mono text-[11px]">{meta?.model ?? 'no turn yet'}</span>
        {meta?.ms !== undefined && (
          <>
            <span aria-hidden className="h-3 w-px bg-border" />
            <span className="font-mono text-[11px] tabular-nums">{ms(meta.ms)}</span>
          </>
        )}
        {/* Only ever shown when something actually failed, so it stays meaningful. */}
        {!!meta?.errorCount && (
          <span className="rounded-full bg-destructive/10 px-1.5 font-mono text-[10px] text-destructive">
            {meta.errorCount} err
          </span>
        )}
        <ChevronDown
          className={`ml-auto h-3.5 w-3.5 shrink-0 transition-transform ${open ? 'rotate-180' : ''}`}
          aria-hidden
        />
      </button>

      {open && (
        <dl
          id="chat-metrics-panel"
          className="grid grid-cols-[auto_minmax(0,1fr)_auto_minmax(0,1fr)] items-baseline gap-x-3 gap-y-1.5 border-t px-3 pb-2.5 pt-2"
        >
          <dt className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Thread</dt>
          <dd className="col-span-3 flex min-w-0 items-center gap-1 font-mono text-[11px] text-foreground">
            <span className="truncate">{id}</span>
            <CopyId value={id} label="thread ID" />
          </dd>

          <ModelPicker />

          {meta?.messageId && (
            <>
              <dt className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Message</dt>
              <dd className="col-span-3 flex min-w-0 items-center gap-1 font-mono text-[11px] text-foreground">
                <span className="truncate">{meta.messageId}</span>
                <CopyId value={meta.messageId} label="message ID" />
              </dd>
            </>
          )}

          {meta ? (
            <>
              <dt className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Model</dt>
              <dd className="col-span-3 min-w-0 truncate font-mono text-[11px] text-foreground">
                {meta.model} · {meta.provider}
              </dd>

              <Row label="Tools">{meta.toolCount}</Row>
              <Row label="Errors">{meta.errorCount}</Row>
              <Row label="Tokens in">{num(meta.usage?.input_tokens)}</Row>
              <Row label="out">{num(meta.usage?.output_tokens)}</Row>
              <Row label="Latency">{ms(meta.ms)}</Row>
              <Row label="Sources">
                {meta.grounded === undefined ? '—' : meta.grounded ? 'grounded' : 'model only'}
              </Row>

              {meta.cappedOut && (
                <dd className="col-span-4 text-[11px] font-sans text-amber-600 dark:text-amber-500">
                  Stopped at the step limit, so part of the request was not carried out.
                </dd>
              )}
            </>
          ) : (
            <dd className="col-span-4 font-sans text-[11px] text-muted-foreground">
              Send a message and the numbers for that turn appear here.
            </dd>
          )}
        </dl>
      )}
    </div>
  )
}
